import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * MetricCard — Clickable stat card with optional delta indicator.
 *
 * Props:
 *   label: string
 *   value: number | string
 *   icon: LucideIcon
 *   accent: string (hex color)
 *   delta: number | null (percentage change; positive = up)
 *   sub: string | null
 *   metricKey: string (used for drill-down identity)
 *   onClick: (metricKey) => void
 *   format: 'number' | 'percent' | 'string'
 */

const MetricCard = ({
    label, value, icon: Icon, accent = '#6366f1',
    delta = null, sub = null, metricKey = '',
    onClick = null, format = 'number',
}) => {
    const displayValue = format === 'percent' && typeof value === 'number'
        ? `${value}%`
        : value ?? '—';

    const isClickable = !!onClick;

    return (
        <div
            onClick={() => isClickable && onClick(metricKey)}
            style={{
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 14,
                padding: '20px 22px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                minWidth: 0,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                transition: 'all 0.2s',
                cursor: isClickable ? 'pointer' : 'default',
                position: 'relative',
                overflow: 'hidden',
            }}
            onMouseOver={e => {
                if (isClickable) {
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                    e.currentTarget.style.borderColor = `${accent}40`;
                    e.currentTarget.style.transform = 'translateY(-1px)';
                }
            }}
            onMouseOut={e => {
                if (isClickable) {
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'none';
                }
            }}
        >
            {/* Header row: icon + label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {Icon && (
                    <div style={{
                        width: 30, height: 30, borderRadius: 8,
                        background: `${accent}14`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Icon size={14} style={{ color: accent }} />
                    </div>
                )}
                <span style={{
                    fontSize: 11, fontWeight: 600, color: '#6b7280',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                    {label}
                </span>
            </div>

            {/* Value row */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <div style={{
                    fontSize: 26, fontWeight: 700, color: '#111827',
                    fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
                }}>
                    {displayValue}
                </div>

                {/* Delta badge */}
                {delta !== null && delta !== undefined && (
                    <DeltaBadge delta={delta} />
                )}
            </div>

            {/* Sub text */}
            {sub && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>{sub}</div>
            )}

            {/* Clickable indicator */}
            {isClickable && (
                <div style={{
                    position: 'absolute', top: 8, right: 10,
                    fontSize: 10, color: '#94a3b8',
                    opacity: 0.6,
                }}>
                    ↗
                </div>
            )}
        </div>
    );
};


const DeltaBadge = ({ delta }) => {
    const isPositive = delta > 0;
    const isNegative = delta < 0;
    const isNeutral = delta === 0;

    const color = isPositive ? '#10b981' : isNegative ? '#ef4444' : '#6b7280';
    const bg = isPositive ? '#ecfdf5' : isNegative ? '#fef2f2' : '#f3f4f6';
    const DeltaIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;

    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: 11, fontWeight: 600, color,
            background: bg, padding: '2px 8px', borderRadius: 6,
        }}>
            <DeltaIcon size={11} />
            {isNeutral ? '0%' : `${isPositive ? '+' : ''}${delta}%`}
        </span>
    );
};


export default MetricCard;
